<template>
    <common-modal width="medium" @emitEvent="emitEvent($event, ['close'])" @close="emitEvent({ name: 'close' })">
        <div class="referral-cancellation-modal">
            <h2 class="referral-cancellation-modal__title">
                {{ $t(`pages.referrals.show.cancellation.${type}.title`, { name: patientFullName }) }}
            </h2>

            <p class="referral-cancellation-modal__description">
                {{ $t('pages.referrals.show.cancellation.description') }}
            </p>

            <label class="referral-cancellation-modal__label">
                {{ $t('pages.referrals.show.cancellation.reason') }}
            </label>

            <div class="referral-cancellation-modal__reasons">
                <label v-for="reason in cancellationReasons" :key="reason.value" class="referral-cancellation-modal__reason">
                    <input v-model="selectedReason" type="radio" name="cancellation-reason" :value="reason.value" />

                    <span class="referral-cancellation-modal__radio"></span>

                    <span class="referral-cancellation-modal__reason-text">
                        {{ reason.label }}
                    </span>
                </label>
            </div>

            <div v-if="isOtherReason" class="referral-cancellation-modal__other">
                <label class="referral-cancellation-modal__label" for="cancellation-other-reason">
                    {{ $t('pages.referrals.show.cancellation.other_reason') }}
                </label>

                <textarea
                    id="cancellation-other-reason"
                    v-model.trim="otherReason"
                    rows="4"
                    class="referral-cancellation-modal__textarea"
                    :placeholder="$t('pages.referrals.show.cancellation.reason_placeholder')"
                ></textarea>
            </div>

            <div class="referral-cancellation-modal__actions">
                <common-button
                    v-if="withSecondOption"
                    :label="$t(`pages.referrals.show.cancellation.${type}.buttons.no`)"
                    type="secondary"
                    @click="emitEvent({ name: 'deny' })"
                ></common-button>
                <common-button
                    :label="$t(`pages.referrals.show.cancellation.${type}.buttons.yes`)"
                    type="primary"
                    :disabled="!isFormValid"
                    @click="submit"
                ></common-button>
            </div>
        </div>
    </common-modal>
</template>

<script>
    export default {
        props: {
            referral: {
                type: Object,
                default: null,
            },
            type: {
                type: String,
                default: 'referred',
            },
            withSecondOption: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                selectedReason: '',
                otherReason: '',
                cancellationReasons: [
                    {
                        value: 'patient_requested',
                        label: 'Patient requested cancellation',
                    },
                    {
                        value: 'patient_unavailable',
                        label: 'Patient is unavailable',
                    },
                    {
                        value: 'appointment_no_longer_needed',
                        label: 'Appointment is no longer needed',
                    },
                    {
                        value: 'referred_to_another_provider',
                        label: 'Patient was referred to another provider',
                    },
                    {
                        value: 'duplicate_referral',
                        label: 'Duplicate referral',
                    },
                    {
                        value: 'insurance_issue',
                        label: 'Insurance or authorization issue',
                    },
                    {
                        value: 'provider_unavailable',
                        label: 'Provider is unavailable',
                    },
                    {
                        value: 'scheduling_conflict',
                        label: 'Scheduling conflict',
                    },
                    {
                        value: 'other',
                        label: 'Other',
                    },
                ],
            }
        },
        computed: {
            patientFullName() {
                return `${this.referral?.patient?.firstName || ''} ${this.referral?.patient?.lastName || ''}`.trim()
            },
            isOtherReason() {
                return this.selectedReason === 'other'
            },

            isFormValid() {
                if (!this.selectedReason) {
                    return false
                }

                if (this.isOtherReason) {
                    return !!this.otherReason
                }

                return true
            },
            closeComment() {
                if (this.isOtherReason) {
                    return this.otherReason
                }

                const selectedReason = this.cancellationReasons.find((reason) => reason.value === this.selectedReason)

                return selectedReason?.label || ''
            },
        },
        methods: {
            submit() {
                if (!this.isFormValid) {
                    return
                }
                this.emitEvent({
                    name: 'confirm',
                    payload: {
                        referralId: this.referral?.id,
                        closeComment: this.closeComment,
                    },
                })
            },
        },
    }
</script>

<style lang="scss" scoped>
    .referral-cancellation-modal {
        padding: 8px 0;
        color: #1f2937;
    }

    .referral-cancellation-modal__title {
        margin: 0 0 10px;
        font-size: 22px;
        line-height: 1.3;
        font-weight: 700;
        color: #1f2937;
    }

    .referral-cancellation-modal__description {
        margin: 0 0 24px;
        font-size: 14px;
        line-height: 1.5;
        color: #6b7280;
    }

    .referral-cancellation-modal__label {
        display: block;
        margin-bottom: 10px;
        font-size: 14px;
        line-height: 1.4;
        font-weight: 600;
        color: #374151;
    }

    .referral-cancellation-modal__reasons {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 20px;
    }

    .referral-cancellation-modal__reason {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        min-height: 46px;
        padding: 11px 14px;
        box-sizing: border-box;

        border: 1px solid #e5e7eb;
        border-radius: 8px;
        background: #ffffff;

        cursor: pointer;
        user-select: none;

        transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

        &:hover {
            border-color: #c7d2fe;
            background-color: #f9fafb;
        }

        input[type='radio'] {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }

        input[type='radio'] + .referral-cancellation-modal__radio {
            display: flex;
            align-items: center;
            justify-content: center;

            flex: 0 0 18px;
            width: 18px;
            height: 18px;
            margin-right: 11px;
            padding: 0;

            box-sizing: border-box;

            border: 2px solid #9ca3af;
            border-radius: 50%;
            background-color: #ffffff;

            transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

            &::after {
                content: '';
                display: block;

                width: 7px;
                height: 7px;

                flex: 0 0 7px;

                border-radius: 50%;
                background-color: #ffffff;

                transform: scale(0);
                transition: transform 0.15s ease;
            }
        }

        input[type='radio']:checked + .referral-cancellation-modal__radio {
            border-color: #2563eb;
            background-color: #2563eb;

            &::after {
                transform: scale(1);
            }
        }

        &:has(input[type='radio']:checked) {
            border-color: #2563eb;
            background-color: #eff6ff;
            box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.08);
        }

        &:has(input[type='radio']:focus-visible) {
            outline: none;
            border-color: #2563eb;
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }
    }

    .referral-cancellation-modal__reason-text {
        flex: 1;
        min-width: 0;

        font-size: 14px;
        line-height: 1.4;
        color: #374151;
    }

    .referral-cancellation-modal__textarea {
        display: block;
        width: 100%;
        min-height: 110px;
        padding: 11px 13px;

        box-sizing: border-box;

        border: 1px solid #d1d5db;
        border-radius: 8px;

        background: #ffffff;
        color: #1f2937;

        font-family: inherit;
        font-size: 14px;
        line-height: 1.5;

        resize: vertical;

        transition: border-color 0.2s ease, box-shadow 0.2s ease;

        &::placeholder {
            color: #9ca3af;
        }

        &:hover {
            border-color: #9ca3af;
        }

        &:focus {
            outline: none;
            border-color: #2563eb;
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }
    }

    // Buttons
    .referral-cancellation-modal__actions {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 10px;

        margin-top: 24px;
        padding-top: 18px;

        border-top: 1px solid #f3f4f6;
    }
</style>
